import { useMemo } from 'react';
import type { AnalystOption } from '../../services/api/flightChanges';
import { HangarSelectField } from '../../components/common/HangarSelectField';
import { FLIGHT_CASES_ANALYST_FILTER_SELECT_ID } from './flightChangesCasesFilterControlSx';
import { UNASSIGNED_ANALYST_FILTER_VALUE } from './analystFilterConstants';
import * as S from './FlightChangesCases.styles';

const EMPTY = '';

export interface AnalystFilterSelection {
    analystId: number | null;
    filterUnassigned: boolean;
}

export interface FlightChangesCasesAnalystFilterProps {
    analystId: number | null;
    filterUnassigned: boolean;
    options: AnalystOption[];
    onChange: (selection: AnalystFilterSelection) => void;
    labelKey: string;
    placeholderKey: string;
    unassignedOptionKey: string;
    t: (key: string) => string;
}

export const FlightChangesCasesAnalystFilter = ({
    analystId,
    filterUnassigned,
    options,
    onChange,
    labelKey,
    placeholderKey,
    unassignedOptionKey,
    t,
}: FlightChangesCasesAnalystFilterProps) => {
    const label = t(labelKey);
    const placeholder = t(placeholderKey);
    const unassignedLabel = t(unassignedOptionKey);

    const hangarOptions = useMemo(
        () => [
            { value: UNASSIGNED_ANALYST_FILTER_VALUE, label: unassignedLabel },
            ...options.map((o) => ({ value: String(o.id), label: o.name })),
        ],
        [options, unassignedLabel],
    );

    const handleChange = (v: string) => {
        if (v === EMPTY) {
            onChange({ analystId: null, filterUnassigned: false });
            return;
        }
        if (v === UNASSIGNED_ANALYST_FILTER_VALUE) {
            onChange({ analystId: null, filterUnassigned: true });
            return;
        }
        const id = Number.parseInt(v, 10);
        onChange({
            analystId: Number.isNaN(id) ? null : id,
            filterUnassigned: false,
        });
    };

    const selectValue = (() => {
        if (filterUnassigned) {
            return UNASSIGNED_ANALYST_FILTER_VALUE;
        }
        if (analystId == null) {
            return EMPTY;
        }
        return String(analystId);
    })();

    const selectId = FLIGHT_CASES_ANALYST_FILTER_SELECT_ID;
    const hangarSelectInstanceKey = filterUnassigned
        ? 'analyst-isUnassigned'
        : analystId == null
          ? 'analyst-none'
          : `analyst-${analystId}`;

    return (
        <S.FilterFieldColumn data-testid="flight-cases-analyst-filter">
            <HangarSelectField
                key={hangarSelectInstanceKey}
                id={selectId}
                label={label}
                placeholder={placeholder}
                leadingEmptyOption={false}
                value={selectValue}
                options={hangarOptions}
                onChange={handleChange}
                data-testid="flight-cases-analyst-filter-select"
            />
        </S.FilterFieldColumn>
    );
};
